import React, { useState } from "react";
import { PerformanceType } from "../../../types/post";
import { Sport, TrainingType } from "../../../types/activity";
import { HiLockClosed } from "react-icons/hi2";
import SearchableSelect from "../../common/SearchableSelect";
import PainInjurySection from "../training-log/PainInjurySection";
import { TrainingTypePicker } from "../training-log/TrainingLogForm";
import Slider, {
  MUSCULAR_DESCRIPTIONS,
  RESPIRATION_DESCRIPTIONS,
} from "../training-log/Slider";
import DurationQuickChips from "../training-log/DurationQuickChips";
import { parseDurationToMinutes } from "../training-log/trainingLogUtils";
import { performanceTypeLabel } from "../../../types/event";

export interface LoadState {
  duration: string;
  muscular: number;
  respiratory: number;
  trainingPerformed: number;
  notes: string;
  pain: boolean;
  injured: boolean;
  niggleOpen: boolean;
  nigglePain: number;
  niggleImpact: number;
  niggleNotes: string;
}

export const initialLoadState = (): LoadState => ({
  duration: "",
  muscular: 1,
  respiratory: 1,
  trainingPerformed: 1,
  notes: "",
  pain: false,
  injured: false,
  niggleOpen: false,
  nigglePain: 1,
  niggleImpact: 1,
  niggleNotes: "",
});

const FloatingField: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, required, children }) => (
  <div className="relative">
    <label className="absolute left-4 top-2 text-[11px] font-medium uppercase tracking-wider text-base-content/50 pointer-events-none z-10">
      {label}
      {required && <span className="text-error"> *</span>}
    </label>
    {children}
  </div>
);

interface LoadStepProps {
  performanceType: PerformanceType;
  state: LoadState;
  onChange: (next: LoadState) => void;
  durationError: string;
  onDurationBlur: () => void;
  /** Resolved catalogue type for Competition / Game Day (read-only chip). */
  fixedTrainingType: TrainingType | null;
  /** Testing: the athlete picks any training type. */
  trainingTypes: TrainingType[];
  sports: Sport[];
  sportFilter: string;
  onSportFilterChange: (sportId: string) => void;
  selectedTrainingTypeId: string | null;
  onTrainingTypeChange: (id: string | null) => void;
  rpe: number;
  load: number;
  /** Edit mode hides the inline niggle (it already exists on the log). */
  allowNiggle: boolean;
}

/** Step 4 — the training-log body: type, duration, effort, pain. */
const LoadStep: React.FC<LoadStepProps> = ({
  performanceType,
  state,
  onChange,
  durationError,
  onDurationBlur,
  fixedTrainingType,
  trainingTypes,
  sports,
  sportFilter,
  onSportFilterChange,
  selectedTrainingTypeId,
  onTrainingTypeChange,
  rpe,
  load,
  allowNiggle,
}) => {
  const set = <K extends keyof LoadState>(key: K, value: LoadState[K]) =>
    onChange({ ...state, [key]: value });
  const showPerformanceRating = rpe > 7;
  const [showPain, setShowPain] = useState(state.pain || state.injured);
  const typeName = performanceTypeLabel(performanceType);

  const filteredTypes = trainingTypes.filter((t) => {
    if (!t.id) return false;
    if (sportFilter === "all") return true;
    const sport = sports.find((s) => s.id === sportFilter);
    if (!sport) return true;
    if (t.isCustomTrainingType) {
      return (
        !t.relatedTrainingTypeID ||
        sport.trainingTypeIDs?.includes(t.relatedTrainingTypeID)
      );
    }
    return sport.trainingTypeIDs?.includes(t.id);
  });

  return (
    <div className="space-y-7">
      <div>
        <h3 className="text-base font-bold font-grotesk text-base-content">
          Load
        </h3>
        <p className="text-sm text-base-content/60 font-grotesk">
          A performance counts as load, just like training.
        </p>
      </div>

      {/* Training type */}
      {performanceType === "testing" ? (
        <div className="space-y-3">
          {sports.length > 1 && (
            <SearchableSelect
              label="Sport"
              options={sports
                .filter((s) => !!s.id)
                .map((s) => ({ value: s.id as string, label: s.name }))}
              value={sportFilter}
              onChange={onSportFilterChange}
              allOptionLabel="All sports"
              emptyValue="all"
              inputClassName="bg-base-200"
            />
          )}
          <TrainingTypePicker
            options={filteredTypes
              .map((t) => ({ value: t.id as string, label: t.name }))
              .sort((a, b) => a.label.localeCompare(b.label))}
            selected={(() => {
              const t = trainingTypes.find((tt) => tt.id === selectedTrainingTypeId);
              return t?.id ? { value: t.id, label: t.name } : null;
            })()}
            onSelect={onTrainingTypeChange}
            placeholder={
              filteredTypes.length ? "Select training type" : "No training types available"
            }
          />
        </div>
      ) : (
        <div className="relative border border-base-300 rounded-md bg-base-200">
          <label className="absolute left-4 top-1 text-xs text-base-content/60 font-grotesk pointer-events-none z-10">
            Training type
          </label>
          <div className="flex items-center gap-2 pl-4 pr-4 pt-6 pb-2 min-h-[3.5rem]">
            <span className="inline-flex items-center p-2 rounded-md text-sm font-geist bg-neutral text-white font-medium">
              {fixedTrainingType?.name ?? typeName}
            </span>
            {fixedTrainingType ? (
              <HiLockClosed
                className="ml-auto w-4 h-4 text-base-content/40"
                aria-label="Set by the performance type"
              />
            ) : (
              <span className="ml-auto text-xs text-base-content/50 font-grotesk">
                Not in catalogue
              </span>
            )}
          </div>
        </div>
      )}

      {/* Duration */}
      <div>
        <FloatingField label="Duration" required>
          <input
            type="text"
            inputMode="numeric"
            value={state.duration}
            onChange={(e) => set("duration", e.target.value)}
            onBlur={onDurationBlur}
            placeholder="60 or 1:30"
            className="w-full px-4 pt-7 pr-14 font-semibold pb-2.5 rounded-xl bg-base-200 border border-base-300 focus:border-base-300 focus:outline-none focus:ring-0 text-[16px] font-geist tabular-nums placeholder:font-sans placeholder:text-base-content/40"
          />
          <span className="absolute right-4 bottom-3 text-sm font-light text-base-content/40 pointer-events-none">
            min
          </span>
        </FloatingField>
        {state.duration.trim() === "" && (
          <DurationQuickChips onSelect={(mins) => set("duration", String(mins))} />
        )}
        {durationError && (
          <p className="text-xs text-error mt-1.5 font-grotesk">{durationError}</p>
        )}
      </div>

      {/* Effort sliders */}
      <div className="space-y-6">
        <Slider
          label="Muscular Effort"
          question="How much muscular effort did you exert?"
          value={state.muscular}
          onChange={(v) => set("muscular", v)}
          description={MUSCULAR_DESCRIPTIONS[state.muscular]}
        />
        <Slider
          label="Respiratory Effort"
          question="How breathless did the performance make you?"
          value={state.respiratory}
          onChange={(v) => set("respiratory", v)}
          description={RESPIRATION_DESCRIPTIONS[state.respiratory]}
        />
      </div>

      <PainInjurySection
        className="-mt-2"
        open={showPain}
        onOpenChange={setShowPain}
        pain={state.pain}
        onPainChange={(v) => set("pain", v)}
        injured={state.injured}
        onInjuredChange={(v) => set("injured", v)}
        allowNiggle={allowNiggle}
        addNiggle={state.niggleOpen}
        onAddNiggleChange={(v) => set("niggleOpen", v)}
        nigglePain={state.nigglePain}
        onNigglePainChange={(v) => set("nigglePain", v)}
        niggleImpact={state.niggleImpact}
        onNiggleImpactChange={(v) => set("niggleImpact", v)}
        niggleNotes={state.niggleNotes}
        onNiggleNotesChange={(v) => set("niggleNotes", v)}
        niggleSubtitle="Logged as a Niggle linked to this performance"
      />

      <div className="flex items-center justify-between rounded-md bg-base-200 px-4 py-3">
        <span className="text-xs font-medium text-base-content/70">RPE</span>
        <span className="font-geist tabular-nums font-bold">{rpe}</span>
        <span className="text-xs font-medium text-base-content/70">Load</span>
        <span className="font-geist tabular-nums font-bold">
          {parseDurationToMinutes(state.duration) > 0 ? load : "—"}
        </span>
      </div>

      {showPerformanceRating && (
        <Slider
          label="Performance rating"
          question="How well did you perform?"
          value={state.trainingPerformed}
          onChange={(v) => set("trainingPerformed", v)}
          description={`How well you performed: ${state.trainingPerformed}/10`}
          min={1}
          max={10}
        />
      )}

    </div>
  );
};

export default LoadStep;
